import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import LineageCanvas from '../../lineage/LineageCanvas';
import { crossSystemService } from '../../../services/crossSystemService';
import { PersonaLineageNode } from '../../../types/crossSystem';

interface Props {
  personaId: string;
}

const LineageNetwork: React.FC<Props> = ({ personaId }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [rootNodes, setRootNodes] = useState<PersonaLineageNode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [ancestors, descendants] = await Promise.all([
          crossSystemService.getAncestors(personaId),
          crossSystemService.getDescendants(personaId),
        ]);

        const currentNode: PersonaLineageNode = {
          persona_id: personaId,
          name: personaId.slice(0, 12) + '...',
          tags: [],
          revision: 0,
          capability_summary: {},
          children: descendants.map((d: any) => ({
            persona_id: d.persona_id,
            name: d.name || d.persona_id.slice(0, 8),
            tags: [],
            revision: 0,
            remix_point: d.remix_point,
            capability_summary: {},
            children: [],
          })),
        };

        setRootNodes([currentNode]);
      } catch (err) {
        console.error('Failed to load lineage:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [personaId]);

  if (loading) return <div className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Loading lineage...</div>;

  return (
    <div>
      {/* Lineage Tree */}
      <div className={`rounded-xl border mb-6 overflow-hidden ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`px-4 pt-4 text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Heritage Tree</h3>
        <div style={{ height: 300 }}>
          <LineageCanvas
            rootNodes={rootNodes}
            focusPersonaId={personaId}
            scope="focused"
            onNavigate={(id) => {
              if (id !== personaId) navigate(`/dashboards/personas/${id}`);
            }}
          />
        </div>
      </div>

      {/* Collaboration Network placeholder */}
      <div className={`rounded-xl border p-6 text-center ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Collaboration Network</h3>
        <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Force-directed graph showing co-assigned personas, workflows, and projects will appear as workflow participation data accumulates.
        </p>
      </div>
    </div>
  );
};

export default LineageNetwork;
